import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { headerBottomPx } from '../utils/headerOverlay';

// Lightweight polling for availability alerts; only while the app is visible (no sockets or push)
const POLL_MS = 60000;
const IN_FLIGHT_MAX_MS = 30000;

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unreadCount: 0, items: [] });
  // Superadmin only: pending supplier-admin requests (same source and count as AdminNav's badge)
  const [pendingAdminCount, setPendingAdminCount] = useState(0);
  const wrapRef = useRef(null);
  // Top edge of the dropdown: the header's real bottom edge, same measurement the Cart drawer uses
  const [headerBottom, setHeaderBottom] = useState(null);

  // One request at a time: a poll, a focus, and a visibility change can overlap. A request that
  // never returns is abandoned after IN_FLIGHT_MAX_MS so polling cannot stop for good.
  const inFlightSince = useRef(0);
  const load = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    if (inFlightSince.current && Date.now() - inFlightSince.current < IN_FLIGHT_MAX_MS) return;
    inFlightSince.current = Date.now();
    const isSuperAdmin = JSON.parse(localStorage.getItem('user') || 'null')?.role === 'superadmin';
    api.get('/api/notifications')
      .then((res) => setData({ unreadCount: res.data?.unreadCount || 0, items: res.data?.items || [] }))
      .catch(() => {})
      .finally(() => { inFlightSince.current = 0; });
    if (isSuperAdmin) {
      api.get('/api/users', { params: { role: 'pending' } })
        .then((res) => setPendingAdminCount(Array.isArray(res.data) ? res.data.length : 0))
        .catch(() => setPendingAdminCount(0));
    }
  }, []);

  // One interval per mounted bell; removed on unmount (logout unmounts the header bell)
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    // Approving or rejecting a request on the Pending Admins page refreshes the count immediately
    window.addEventListener('pending-admins-changed', load);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener('pending-admins-changed', load);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [load]);

  useEffect(() => {
    if (open) setHeaderBottom(headerBottomPx());
  }, [open]);

  // Close the popover on an outside click or Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markAllRead = () => {
    setData((d) => ({ unreadCount: 0, items: d.items.map((i) => ({ ...i, read: true })) }));
    api.post('/api/notifications/read', {}).catch(() => {});
  };

  const openAlert = (item) => {
    if (!item.read) {
      setData((d) => ({
        unreadCount: Math.max(0, d.unreadCount - 1),
        items: d.items.map((i) => (i._id === item._id ? { ...i, read: true } : i)),
      }));
      api.post('/api/notifications/read', { ids: [item._id] }).catch(() => {});
    }
    setOpen(false);
    navigate(`/GroupSearchAndJoin?groupId=${item.groupId}`);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('notify.bellLabel')}
        aria-expanded={open}
        className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center hover:text-green-700"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {data.unreadCount > 0 && (
          <span data-testid="unread-count" className="absolute -right-0.5 -top-0.5 rounded-full bg-red-600 px-1.5 text-[10px] leading-4 text-white">
            {data.unreadCount > 9 ? '9+' : data.unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div style={headerBottom != null ? { top: headerBottom } : undefined} className="fixed left-3 right-3 z-50 flex max-h-[min(350px,calc(100vh-5rem))] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-sm text-gray-800 shadow-lg sm:left-auto sm:right-0 sm:w-80 sm:max-h-[350px]">
          <div className="flex min-w-0 shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
            <span className="font-semibold">{t('notify.title')}</span>
            {data.unreadCount > 0 && (
              <button type="button" onClick={markAllRead} className="shrink-0 text-xs text-blue-600 hover:underline">{t('notify.markAllRead')}</button>
            )}
          </div>
          {pendingAdminCount > 0 && (
            <div className="shrink-0 border-b px-3 py-2">
              <div className="mb-1 text-xs font-semibold uppercase text-gray-500">{t('adminPending.title')}</div>
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/admin/pending-admins'); }}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-2 text-left hover:bg-gray-50"
              >
                <span className="min-w-0 break-words font-medium">{t('nav.pendingAdmins')}</span>
                <span className="shrink-0 rounded-full bg-red-600 px-2 text-xs leading-5 text-white">{pendingAdminCount}</span>
              </button>
            </div>
          )}
          {data.items.length === 0 && pendingAdminCount === 0 ? (
            <p className="p-3 text-sm text-gray-600">{t('notify.empty')}</p>
          ) : (
            <ul className="min-h-0 overflow-y-auto">
              {data.items.map((item) => (
                <li key={item._id}>
                  <button type="button" onClick={() => openAlert(item)} className={`w-full px-3 py-2 text-left hover:bg-gray-50 ${item.read ? '' : 'bg-blue-50'}`}>
                    <div className="break-words text-sm font-medium">{item.groupName ? t('notify.available', { group: item.groupName }) : t('notify.groupRemoved')}</div>
                    <div className="break-words text-xs text-gray-500">
                      {new Date(item.createdAt).toLocaleString(i18n.language)} · {item.read ? t('notify.read') : t('notify.unread')}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
